"use client";

import { useState, useEffect, useMemo } from "react";
import { EmbedHeader } from "@fm/ui";
import { getBusinessProfile, type ConfigBusinessProfile } from "@fm/api";
import type { FleetBusinessProfile } from "@fm/api";
import { getApiBaseUrl } from "../lib/config";
import { buildBookingHref } from "../utils/bookingPage";
import { useFleetRegions } from "../hooks/useFleetRegions";
import { EmbedSearchBar, type EmbedSearchBarFormData } from "./EmbedSearchBar";

function toConfigProfile(profile: FleetBusinessProfile): ConfigBusinessProfile {
  return {
    name: profile.name,
    logo_url: profile.logo_url,
    phone: profile.phone,
    country_code: profile.country_code,
    website_url: profile.website_url,
    street_address: profile.street_address,
    city: profile.city,
    state: profile.state,
    zip_code: profile.zip_code,
  };
}

export interface EmbedHeaderContainerProps {
  apiKey: string;
  apiBaseUrl?: string;
  defaultLogoUrl?: string;
  googleMapsApiKey?: string;
  primaryColor?: string;
  theme?: "light" | "dark";
  initialSearchData?: Partial<EmbedSearchBarFormData>;
  onSearch?: (data: EmbedSearchBarFormData) => void;
  className?: string;
  /** Open the logo link (to the booking page) in a new tab. */
  openInNewTab?: boolean;
  /** Absolute app origin for the booking-page link (needed for web components
   *  on third-party sites; omit inside same-origin iframes). */
  appBaseUrl?: string;
}

export function EmbedHeaderContainer({
  apiKey,
  apiBaseUrl,
  defaultLogoUrl = "/fleetsync-logo.png",
  googleMapsApiKey,
  primaryColor,
  initialSearchData,
  onSearch,
  className,
  openInNewTab,
  appBaseUrl,
}: EmbedHeaderContainerProps) {
  const [businessProfile, setBusinessProfile] =
    useState<ConfigBusinessProfile | null>(null);
  const [slug, setSlug] = useState<string | null>(null);
  const [customDomain, setCustomDomain] = useState<string | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  // Regions and their resolved pickup defaults, learned from the widget's
  // own bootstrap endpoint rather than handed in — see `useFleetRegions`.
  // Never gates rendering: a slow or failing fetch just means no picker,
  // never a blocked or broken header.
  const { regions, defaultPickup } = useFleetRegions(apiKey, apiBaseUrl);

  const logoUrl = businessProfile?.logo_url || defaultLogoUrl;
  const logoAlt = businessProfile?.name || "FleetSync";
  const mapsKey =
    googleMapsApiKey || process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "";

  const apiClient = useMemo(
    () => ({
      apiKey,
      baseUrl: getApiBaseUrl(apiBaseUrl),
    }),
    [apiKey, apiBaseUrl],
  );

  useEffect(() => {
    const loadProfile = async () => {
      if (!apiKey) {
        setIsLoadingProfile(false);
        return;
      }
      try {
        const profile = await getBusinessProfile(apiClient);
        if (profile) {
          setBusinessProfile(toConfigProfile(profile));
          setSlug(profile.slug ?? null);
          setCustomDomain(profile.custom_domain ?? null);
        }
      } catch (error) {
        console.error("Failed to load business profile for header:", error);
      } finally {
        setIsLoadingProfile(false);
      }
    };
    loadProfile();
  }, [apiKey, apiClient]);

  return (
    <EmbedHeader
      logoUrl={logoUrl}
      defaultLogoUrl={defaultLogoUrl}
      logoAlt={logoAlt}
      logoHref={buildBookingHref(slug, appBaseUrl, customDomain)}
      openInNewTab={openInNewTab}
      phone={businessProfile?.phone}
      countryCode={businessProfile?.country_code}
      className={className}
      isLoading={isLoadingProfile}
    >
      {mapsKey ? (
        <EmbedSearchBar
          googleMapsApiKey={mapsKey}
          primaryColor={primaryColor}
          onSearch={onSearch}
          initialData={initialSearchData}
          regions={regions}
          defaultPickup={defaultPickup}
        />
      ) : null}
    </EmbedHeader>
  );
}
